import Microgame from '../engine/Microgame.js';

export default class MazeGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Labyrinthe !`, en: `Maze!`, es: `¡Laberinto!` },
            controls: { fr: `Sors du labyrinthe avant la fin (glisse ou utilise les flèches)`, en: `Find the exit before time ends (swipe or arrow keys)`, es: `Encuentra la salida antes de que acabe el tiempo (desliza o flechas)` },
            objective: { fr: `Atteins la zone de sortie verte`, en: `Reach the green exit zone`, es: `Llega a la zona verde de salida` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="M3 3v18h18"/><path d="M3 9h8v4H7"/><path d="M15 3v8h4"/><path d="M11 21v-4h4"/></svg>`,
            actions: [
                { type: 'wait', duration: 280 },
                { type: 'swipe', fromX: 14, fromY: 20, toX: 86, toY: 80, duration: 1200 },
                { type: 'wait', duration: 700 },
            ],
            loopDuration: 3200
        };
    }
    constructor(engine) {
        super(engine);
        this.walls = [];
        this.goal = null;
        this.player = null;
        this.isDragging = false;
        this.isDragging = false;
        this.duration = 18000; // Give player more time (18s base, 4.5s at x4 speed)
        this.collisions = 0;
        this.isInvulnerable = false;
    }

    setup() {
        this.engine.ui.instruction.textContent = "ESCAPE!";

        // Create Maze Container
        this.mazeContainer = document.createElement('div');
        this.mazeContainer.style.position = 'absolute';
        this.mazeContainer.style.width = '300px';
        this.mazeContainer.style.height = '400px';
        this.mazeContainer.style.top = '50%';
        this.mazeContainer.style.left = '50%';
        this.mazeContainer.style.transform = 'translate(-50%, -50%)';
        this.mazeContainer.style.border = `2px solid ${this.primaryColor}`;
        this.container.appendChild(this.mazeContainer);

        // Maze Parameters
        const cols = 5;
        const rows = 7;
        const cellW = 300 / cols;
        const cellH = 400 / rows;

        // Generate Maze (Recursive Backtracker)
        const maze = []; // [col][row] -> { right: true, bottom: true }
        for (let x = 0; x < cols; x++) {
            maze[x] = [];
            for (let y = 0; y < rows; y++) {
                maze[x][y] = { visited: false, right: true, bottom: true };
            }
        }

        const stack = [];
        const start = { x: 0, y: 0 };
        maze[start.x][start.y].visited = true;
        stack.push(start);

        while (stack.length > 0 && !this.isPlayback) {
            const current = stack[stack.length - 1];
            const neighbors = [];

            // Check neighbors (Right, Left, Down, Up)
            if (current.x + 1 < cols && !maze[current.x + 1][current.y].visited) neighbors.push({ x: current.x + 1, y: current.y, dir: 'right' });
            if (current.x - 1 >= 0 && !maze[current.x - 1][current.y].visited) neighbors.push({ x: current.x - 1, y: current.y, dir: 'left' });
            if (current.y + 1 < rows && !maze[current.x][current.y + 1].visited) neighbors.push({ x: current.x, y: current.y + 1, dir: 'down' });
            if (current.y - 1 >= 0 && !maze[current.x][current.y - 1].visited) neighbors.push({ x: current.x, y: current.y - 1, dir: 'up' });

            if (neighbors.length > 0) {
                const next = neighbors[Math.floor(Math.random() * neighbors.length)];

                // Remove Wall
                if (next.dir === 'right') maze[current.x][current.y].right = false;
                else if (next.dir === 'left') maze[next.x][next.y].right = false; // Remove right wall of LEFT neighbor
                else if (next.dir === 'down') maze[current.x][current.y].bottom = false;
                else if (next.dir === 'up') maze[next.x][next.y].bottom = false; // Remove bottom wall of UPPER neighbor

                maze[next.x][next.y].visited = true;
                stack.push(next);
            } else {
                stack.pop();
            }
        }

        // Render Walls
        const wallThickness = 4;

        // Internal Walls from Maze Data
        for (let x = 0; x < cols; x++) {
            for (let y = 0; y < rows; y++) {
                const cell = maze[x][y];
                const px = x * cellW;
                const py = y * cellH;

                // Right Wall
                if (cell.right && x < cols - 1) { // Don't draw outer boundary here (it's the container border)
                    this.createWall(px + cellW - wallThickness / 2, py, wallThickness, cellH);
                }

                // Bottom Wall
                if (cell.bottom && y < rows - 1) {
                    this.createWall(px, py + cellH - wallThickness / 2, cellW, wallThickness);
                }
            }
        }

        // Create Goal
        this.goal = document.createElement('div');
        this.goal.style.position = 'absolute';
        this.goal.style.bottom = '10px';
        this.goal.style.right = '10px';
        this.goal.style.width = '40px';
        this.goal.style.height = '40px';
        this.goal.style.backgroundColor = this.secondaryColor;
        this.goal.style.borderRadius = '50%';
        this.goal.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        this.goal.textContent = 'GO';
        this.goal.style.display = 'flex';
        this.goal.style.alignItems = 'center';
        this.goal.style.justifyContent = 'center';
        this.goal.style.color = 'black';
        this.goal.style.fontWeight = 'bold';
        this.mazeContainer.appendChild(this.goal);

        // Create Player
        this.player = document.createElement('div');
        this.player.style.position = 'absolute';
        this.player.style.top = '10px';
        this.player.style.left = '10px';
        this.player.style.width = '18px';
        this.player.style.height = '18px';
        this.player.style.backgroundColor = this.primaryColor;
        this.player.style.borderRadius = '50%';
        this.player.style.boxShadow = `0 0 15px ${this.primaryColor}`;
        this.player.style.cursor = 'grab';
        this.player.style.zIndex = '10';
        this.mazeContainer.appendChild(this.player);

        // Events
        this.onMove = this.onMove.bind(this);
        this.onDown = this.onDown.bind(this);
        this.onUp = this.onUp.bind(this);

        // Bind to container to catch events
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    onDown(e) {
        if (!this.active) return;

        // Check distance to player for easier grabbing
        const rect = this.player.getBoundingClientRect();
        const center = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
        };

        const dist = Math.hypot(e.clientX - center.x, e.clientY - center.y);

        if (dist < 40) { // 40px radius tolerance
            this.isDragging = true;
            this.player.style.cursor = 'grabbing';
            // Snap offset to center if needed, or keep relative?
            // Relative is smoother usually, but for maze, center snap might be better. 
            // Let's stick to current logic which uses offset in onMove.
            // We need to trigger onMove logic correctly.
            // Actually, existing onMove logic relies on `e.target` offset if using `e.offsetX` etc?
            // Let's check onMove.
        }
    }

    onUp(e) {
        this.isDragging = false;
        this.player.style.cursor = 'grab';
    }

    onMove(e) {
        if (!this.isDragging || !this.active) return;

        const rect = this.mazeContainer.getBoundingClientRect();
        const x = e.clientX - rect.left - 9; // Center offset (half of 18px)
        const y = e.clientY - rect.top - 9;

        // Boundary checks
        if (x < 0 || x > 270 || y < 0 || y > 370) {
            this.handleCollision(x, y);
            return;
        }

        this.player.style.left = `${x}px`;
        this.player.style.top = `${y}px`;

        this.checkCollisions(x, y);
    }

    checkCollisions(x, y) {
        const pRect = this.player.getBoundingClientRect();

        // Check Walls
        for (const wall of this.walls) {
            const wRect = wall.getBoundingClientRect();
            if (this.isColliding(pRect, wRect)) {
                this.handleCollision(x, y);
                return;
            }
        }

        // Check Goal
        const gRect = this.goal.getBoundingClientRect();
        if (this.isColliding(pRect, gRect)) {
            this.win();
        }
    }

    isColliding(r1, r2) {
        return !(r2.left > r1.right ||
            r2.right < r1.left ||
            r2.top > r1.bottom ||
            r2.bottom < r1.top);
    }

    handleCollision(x, y) {
        if (this.isInvulnerable) return;

        this.collisions++;
        if (this.collisions >= 2) {
            this.lose();
        } else {
            // Draw a cross at collision site
            const cross = document.createElement('div');
            cross.style.position = 'absolute';
            cross.style.left = `${x}px`;
            cross.style.top = `${y}px`;
            cross.style.width = '18px';
            cross.style.height = '18px';
            cross.style.pointerEvents = 'none'; // Don't block interactions
            cross.style.zIndex = '5';

            // Draw cross using secondaryColor
            cross.innerHTML = `
                <div style="position: absolute; top: 8px; left: 0; width: 18px; height: 3px; background-color: ${this.secondaryColor}; transform: rotate(45deg);"></div>
                <div style="position: absolute; top: 8px; left: 0; width: 18px; height: 3px; background-color: ${this.secondaryColor}; transform: rotate(-45deg);"></div>
            `;
            this.mazeContainer.appendChild(cross);

            // Visual feedback (Flash Red)
            this.player.style.backgroundColor = 'red';
            this.engine.ui.instruction.textContent = "ATTENTION! (1 ERREUR)";

            this.isInvulnerable = true;
            setTimeout(() => {
                if (this.player && this.active) {
                    this.player.style.backgroundColor = this.primaryColor;
                    this.isInvulnerable = false;
                }
            }, 500);
        }
    }

    createWall(x, y, w, h) {
        const wall = document.createElement('div');
        wall.style.position = 'absolute';
        wall.style.left = `${x}px`;
        wall.style.top = `${y}px`;
        wall.style.width = `${w}px`;
        wall.style.height = `${h}px`;
        wall.style.backgroundColor = this.secondaryColor;
        wall.style.boxShadow = `0 0 5px ${this.secondaryColor}`;
        this.mazeContainer.appendChild(wall);
        this.walls.push(wall);
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
